import { useState, useEffect, useCallback } from 'react';
import { api, errMessage, toastError } from '../utils/api';
import type {
  Project,
  ProjectWithDetails,
  ProjectDetailResponse,
  UpdateProjectRequest,
  CreateStoryElementRequest,
  UpdateStoryElementRequest,
  StoryElement,
} from '@historyjki/shared';
import { useToast } from './useToast';

interface UseProjectReturn {
  project: ProjectWithDetails | null;
  loading: boolean;
  error: string | null;
  /** Re-reads the project; resolves with the fresh copy (null on failure)
   *  so callers can build on it without waiting for a state flush. */
  refetch: () => Promise<ProjectWithDetails | null>;
  // Mutations resolve with a success signal instead of throwing: a boolean
  // (true = persisted), or the created entity / null. The hook already toasts
  // on failure, so callers branch on the result to run their success UI
  // (close a modal, show a success toast, navigate) without risking an
  // unhandled promise rejection.
  updateProject: (data: UpdateProjectRequest) => Promise<boolean>;
  addStoryElement: (data: CreateStoryElementRequest) => Promise<StoryElement | null>;
  updateStoryElement: (elementId: string, data: UpdateStoryElementRequest) => Promise<boolean>;
  deleteStoryElement: (elementId: string) => Promise<boolean>;
}

export function useProject(projectId: string | undefined): UseProjectReturn {
  const [project, setProject] = useState<ProjectWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const fetchProject = useCallback(async (): Promise<ProjectWithDetails | null> => {
    if (!projectId) return null;
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<ProjectDetailResponse>(`/projects/${projectId}`);
      setProject(data.project);
      return data.project;
    } catch (err) {
      const message = errMessage(err, 'Failed to load project');
      setError(message);
      showToast(message, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId, showToast]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const updateProject = useCallback(async (data: UpdateProjectRequest): Promise<boolean> => {
    if (!projectId) return false;
    try {
      const resp = await api.patch<{ project: Project }>(`/projects/${projectId}`, data);
      setProject((prev) => prev ? { ...prev, ...resp.project } : prev);
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to update project');
      return false;
    }
  }, [projectId, showToast]);

  const addStoryElement = useCallback(async (data: CreateStoryElementRequest): Promise<StoryElement | null> => {
    if (!projectId) return null;
    try {
      const resp = await api.post<{ story_element: StoryElement }>(
        `/projects/${projectId}/story-elements`,
        data,
      );
      await fetchProject();
      return resp.story_element;
    } catch (err) {
      toastError(showToast, err, 'Failed to add story element');
      return null;
    }
  }, [projectId, fetchProject, showToast]);

  const updateStoryElement = useCallback(async (elementId: string, data: UpdateStoryElementRequest): Promise<boolean> => {
    if (!projectId) return false;
    try {
      await api.patch(`/projects/${projectId}/story-elements/${elementId}`, data);
      await fetchProject();
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to update story element');
      return false;
    }
  }, [projectId, fetchProject, showToast]);

  const deleteStoryElement = useCallback(async (elementId: string): Promise<boolean> => {
    if (!projectId) return false;
    try {
      await api.delete(`/projects/${projectId}/story-elements/${elementId}`);
      await fetchProject();
      showToast('Story element deleted', 'success');
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to delete story element');
      return false;
    }
  }, [projectId, fetchProject, showToast]);

  return {
    project,
    loading,
    error,
    refetch: fetchProject,
    updateProject,
    addStoryElement,
    updateStoryElement,
    deleteStoryElement,
  };
}
